import React from 'react'
import styled, {css} from 'styled-components'

import type {Props as RichTextProps} from '@/components/rich-text'
import RichText, {hasRichText} from '@/components/rich-text'
import {Text} from '@/components/text'
import {VisuallyHidden} from '@/components/visually-hidden'
import {tokens, colors} from '@/theme'
import mq from '@/utils/media-query'

const MAX_COLUMNS = 7

type Cell = RichTextProps['content']

type TableDataRow = {
    heading?: string
    cells: Cell[]
}

type TableData = {
    columnHeadings: string[]
    rows: TableDataRow[]
}

export type TableProps = {
    title?: string
    tableData: TableData
}

export const Table = ({title, tableData}: TableProps) => {
    const columnHeadings = tableData.columnHeadings.filter(hasRichText).slice(0, MAX_COLUMNS)
    // If the table has more than 2 columns then we allow the table to scroll
    // horizontally on smaller viewports
    const shouldScrollHorizontally = columnHeadings.length > 2

    return (
        <Wrapper>
            {title && (
                <Text as="h2" $variant="paragraph-large-medium">
                    {title}
                </Text>
            )}

            <OverflowWrapper>
                <StyledTable $shouldScrollHorizontally={shouldScrollHorizontally}>
                    {title && <VisuallyHidden as="caption">{title}</VisuallyHidden>}

                    <TBody>
                        <tr>
                            {/* If the table has row headings, we render an empty cell in the top left */}
                            {tableData.rows.some((row) => !!row.heading) && <TH scope="row" />}

                            {columnHeadings.map((columnHeading, columnHeadingIndex) => (
                                <TH key={columnHeadingIndex} scope="col">
                                    <Text $variant="label-base-medium" $color="muted">
                                        {columnHeading}
                                    </Text>
                                </TH>
                            ))}
                        </tr>

                        {tableData.rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {!!row.heading && (
                                    <TH scope="row">
                                        <Text $variant="label-base-medium" $color="muted">
                                            {row.heading}
                                        </Text>
                                    </TH>
                                )}

                                {row.cells
                                    .filter(hasRichText)
                                    // If the row has a heading, deduct that column from the max number of columns
                                    .slice(0, !!row.heading ? MAX_COLUMNS - 1 : MAX_COLUMNS)
                                    .map((cell, cellIndex) => (
                                        <TD key={cellIndex}>
                                            <RichText
                                                content={cell}
                                                $variant="label-base"
                                                $color="muted"
                                            />
                                        </TD>
                                    ))}
                            </tr>
                        ))}
                    </TBody>
                </StyledTable>
            </OverflowWrapper>
        </Wrapper>
    )
}

const overflowBreakpoint = 'xs'

const cellStyles = css`
    padding: ${tokens.spacing12} ${tokens.spacing6};
    text-align: left;
    vertical-align: top;
    background: ${colors.background.default};
    border-bottom: 1px solid ${colors.border.dark};

    &:first-child {
        padding-left: 0;
    }

    &:last-child {
        padding-right: 0;
    }

    tr:first-child & {
        border-top: 1px solid ${colors.border.dark};
    }

    &[scope='row'] {
        ${mq.max(overflowBreakpoint)} {
            position: sticky;
            left: 0;
        }
    }

    ${mq.min('md')} {
        padding: ${tokens.spacing20} ${tokens.spacing6};
    }

    ${mq.min('xl')} {
        padding: ${tokens.spacing24} ${tokens.spacing16};
    }

    ul {
        padding-left: ${tokens.spacing14};
        row-gap: ${tokens.spacing4};
    }
`

const Wrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing12};

    ${mq.min('md')} {
        row-gap: ${tokens.spacing20};
    }
`

const OverflowWrapper = styled.div`
    ${mq.max(overflowBreakpoint)} {
        overflow-x: auto;
    }
`

const horizontalScrollStyles = css`
    ${mq.max(overflowBreakpoint)} {
        width: 600px;
    }
`

type StyledTableProps = {
    $shouldScrollHorizontally: boolean
}

const StyledTable = styled.table<StyledTableProps>`
    width: 100%;
    table-layout: fixed;
    border-spacing: 0;
    border-collapse: separate;

    ${(props) => props.$shouldScrollHorizontally && horizontalScrollStyles}
`

const TBody = styled.tbody`
    width: 100%;
`

const TD = styled.td`
    ${cellStyles}
`

const TH = styled.th`
    ${cellStyles}
`
